import { useRef, useEffect, useCallback } from 'react'
import { reportError } from 'utils/error'

interface UseAudioPlaybackProps {
  audioUrl: string
  musicStartTime: number
  musicEndTime: number
  videoDuration: number
}

interface UseAudioPlaybackReturn {
  playAudioFromTime: (time: number) => void
  stopAudio: () => void
}

export const useAudioPlayback = ({
  audioUrl,
  musicStartTime,
  musicEndTime,
  videoDuration,
}: UseAudioPlaybackProps): UseAudioPlaybackReturn => {
  const audioContextRef = useRef<AudioContext | null>(null)
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const audioBufferRef = useRef<AudioBuffer | null>(null)

  // Load audio buffer
  useEffect(() => {
    const loadAudio = async () => {
      try {
        const response = await fetch(audioUrl)
        const arrayBuffer = await response.arrayBuffer()
        const audioContext = new AudioContext()
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
        audioBufferRef.current = audioBuffer
        audioContext.close()
      } catch (error) {
        reportError(error)
      }
    }

    loadAudio()
  }, [audioUrl])

  const stopAudio = useCallback(() => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop()
      } catch (e) {
        // Audio might already be stopped
      }
      audioSourceRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
  }, [])

  const playAudioFromTime = useCallback(
    (currentTime: number) => {
      // Stop any existing audio first
      stopAudio()

      if (!audioBufferRef.current) return

      const effectiveStartTime = Math.max(0, musicStartTime)
      const effectiveEndTime = Math.min(videoDuration, musicEndTime)

      // Check if current time is within the music range
      if (currentTime >= effectiveStartTime && currentTime < effectiveEndTime) {
        const audioContext = new AudioContext()
        audioContextRef.current = audioContext

        const source = audioContext.createBufferSource()
        source.buffer = audioBufferRef.current
        source.connect(audioContext.destination)

        // Calculate offset into the audio based on current time
        // If musicStartTime is negative, we start partway through the audio
        const offset = currentTime - musicStartTime
        const duration = effectiveEndTime - currentTime

        source.start(0, Math.max(0, offset), duration)
        audioSourceRef.current = source

        source.onended = () => {
          audioSourceRef.current = null
        }
      }
    },
    [musicStartTime, musicEndTime, videoDuration, stopAudio],
  )

  // Cleanup on unmount
  useEffect(() => {
    return stopAudio
  }, [stopAudio])

  return {
    playAudioFromTime,
    stopAudio,
  }
}
